import { Box, makeStyles, Theme, useMediaQuery, useTheme } from '@freighthub/silhouette-core'
import { PortOrAddress } from 'features/Directory'
import { ISearchShipment } from 'features/ShipmentsList'
import { findLastIndex } from 'lodash-es'
import { FC } from 'react'
import { useI18n } from 'utils/localisation'

import { IShipTpEvent } from '../../domain/TransportPlanEvent'
import { getContainersCargoReady, getContainersEvents, getMainStepsCount, ProgressPdad } from '../../service/PDAD'
import { ITransportPlan2, tpPointToStr } from '../../service/TransportPlan2'
import { TpProgressInfoMobile } from './TpProgressInfoMobile'
import { TpProgressSegment } from './TpProgressSegment'

const [PICKUP, DEPARTURE, ARRIVAL, DELIVERY] = [1, 2, 3, 4]

const getEventTime = (event: IShipTpEvent): number => {
  const date = event && event.aggregatedTimestamp ? new Date(event.aggregatedTimestamp.date) : null
  // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
  return date ? date.getTime() : null
}

const getPercentage = (startEvent: IShipTpEvent, endEvent: IShipTpEvent): number => {
  if (!startEvent || !endEvent || (startEvent && !startEvent.actual)) {
    return 0
  }

  if (endEvent.actual) {
    return 100
  }

  const currentTime: number = Date.now()
  const startTime: number = getEventTime(startEvent)
  const endTime: number = getEventTime(endEvent)

  return startTime === null || endTime === null
    ? 0
    : Math.min(90, (100 * (currentTime - startTime)) / (endTime - startTime))
}

const locationTypeByIndex: Record<number, string> = {
  [DEPARTURE]: 'shipments_list-progress-pol',
  [ARRIVAL]: 'shipments_list-progress-pod',
}

const pdadTypeByIndex: Record<number, string> = {
  [PICKUP]: 'pickup',
  [DEPARTURE]: 'departure',
  [ARRIVAL]: 'arrival',
  [DELIVERY]: 'delivery',
}

const useStyles = makeStyles<Theme>((theme) => ({
  transportPlan: {
    minWidth: 0,
    transform: 'translateX(6.25%)',
  },
  [theme.breakpoints.down('sm')]: {
    transportPlan: {
      display: 'none',
    },
  },
}))

export type TpProgressProps = {
  pdadSequence: ProgressPdad[]
  tp: ITransportPlan2
  highlight: Partial<ISearchShipment>
}

type Maybe<T> = undefined | T

export const TpProgress: FC<TpProgressProps> = ({ pdadSequence, tp, highlight }) => {
  const classes = useStyles()
  const { translate } = useI18n()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const events = getContainersEvents(tp)
  const hasPreCarriage: boolean = Boolean(tp.pre && tp.pre.length)
  const hasOnCarriage: boolean = Boolean(tp.on && tp.on.length)

  const mainStepsCount: number = getMainStepsCount(pdadSequence)
  // ignore transshipments
  const pdads: ProgressPdad[] = pdadSequence.filter((pdad, index) => index <= 2 || index > mainStepsCount)
  const lastActualIndex = findLastIndex(
    pdads,
    // @ts-expect-error TS(2345) FIXME: Argument of type '({ event }: ProgressPdad) => boo... Remove this comment to see the full error message
    ({ event }) => event && event.aggregatedTimestamp && event.actual === true
  )
  const isDelivered: boolean = lastActualIndex === DELIVERY || (lastActualIndex === ARRIVAL && !hasOnCarriage)

  let nextIndex: number
  if (lastActualIndex === -1) {
    nextIndex = hasPreCarriage ? PICKUP : DEPARTURE
  } else if (isDelivered) {
    nextIndex = hasOnCarriage ? DELIVERY : ARRIVAL
  } else {
    nextIndex = lastActualIndex + 1
  }

  const crdEvents: IShipTpEvent[] = getContainersCargoReady(tp)
  // @ts-expect-error TS(2322) FIXME: Type 'number | boolean' is not assignable to type ... Remove this comment to see the full error message
  const isCrdNext: boolean =
    crdEvents.length && crdEvents.every((event) => !!event.aggregatedTimestamp && event.actual !== true)

  // @ts-expect-error TS(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
  const isEventComplete = (event: Maybe<IShipTpEvent>): boolean => event && event.actual === true
  const getLocation = (index: number): PortOrAddress => {
    switch (index) {
      case PICKUP:
        // @ts-expect-error TS(2322) FIXME: Type 'Address | null | undefined' is not assignabl... Remove this comment to see the full error message
        return hasPreCarriage ? tp.pre[0].milestones?.pickup?.address : null
      case DEPARTURE:
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        return tp.main[0].route.journey.origin
      case ARRIVAL:
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        return tp.main[0].route.journey.destination
      case DELIVERY:
        // @ts-expect-error TS(2322) FIXME: Type 'Address | null | undefined' is not assignabl... Remove this comment to see the full error message
        return hasOnCarriage ? tp.on[0].milestones?.delivery?.address : null
      default:
        // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'PortOrAddre... Remove this comment to see the full error message
        return null
    }
  }

  const isIndexHighlighted = (index: number): boolean => {
    if (!highlight) {
      return false
    }
    switch (index) {
      case PICKUP:
        return !!highlight.origin
      case DEPARTURE:
        return !!highlight.pol
      case ARRIVAL:
        return !!highlight.pod
      case DELIVERY:
        return !!highlight.destination
      default:
        return false
    }
  }

  const hasPickup = Boolean(pdads[PICKUP]?.event)
  const hasDelivery = Boolean(pdads[DELIVERY]?.event)

  return (
    <>
      {isMobile && (
        <TpProgressInfoMobile
          nextMilestone={translate(pdads[nextIndex].stepUI.tooltipTerm)}
          nextLocation={tpPointToStr(getLocation(nextIndex)) || translate(locationTypeByIndex[nextIndex])}
          isDelivered={isDelivered}
          events={events ? events[pdadTypeByIndex[nextIndex]] : [pdads[nextIndex].event]}
          crdEvents={crdEvents}
          isCrdNext={isCrdNext}
        />
      )}
      <Box display="flex" justifyContent="flex-end" alignItems="flex-start" flex="4" className={classes.transportPlan}>
        {pdads.map(({ stepUI, event }, index) => {
          if (!hasPickup && index === PICKUP) return
          if (!hasDelivery && index === DELIVERY) return
          const isFirst = index === 0
          const isOriginPort = index === 2
          const isDestinationPort = index === pdads.length - 2
          const isLast = index === pdads.length - (!hasDelivery && index === ARRIVAL ? 2 : 1)
          const isPort = isOriginPort || isDestinationPort

          const isActiveCrd = !!crdEvents.length && crdEvents.every((event) => !!event.aggregatedTimestamp) && isFirst
          const isActualCrd = !!crdEvents.length && crdEvents.every((event) => !!event.actual) && isFirst
          const isIconDisabled = (isFirst && !isActiveCrd) || (hasDelivery && isLast && !hasOnCarriage)
          const isStepDisabled = (isFirst && !isActiveCrd) || (isDestinationPort && !hasOnCarriage)
          let eventTerm: string | undefined
          if (event?.aggregatedTimestamp?.confirmed && stepUI.confirmedLabelTerm) {
            eventTerm = stepUI.confirmedLabelTerm
          } else {
            eventTerm = event?.actual || !event?.aggregatedTimestamp ? stepUI.titleTerm : stepUI.estimatedLabelTerm
          }
          const isLocationHighlighted = isIndexHighlighted(index)
          const isStepComplete =
            isEventComplete(event) ||
            isActualCrd ||
            (isActiveCrd && pdads[index + 1] && isEventComplete(pdads[index + 1].event))
          const showDelay = !isDelivered
          const locationIndex = !hasPickup && isFirst ? index + 1 : index
          const locationText = tpPointToStr(getLocation(locationIndex)) || translate(locationTypeByIndex[locationIndex])

          const currentEvent = events ? events[pdadTypeByIndex[index]] : [event]

          return (
            <TpProgressSegment
              key={stepUI.tooltipTerm}
              icon={isPort ? stepUI.icon : 'storehouse'}
              size={isPort ? 20 : 24}
              tooltip={!isStepComplete && eventTerm ? translate(eventTerm) : translate(stepUI.tooltipTerm)}
              isIconDisabled={isIconDisabled}
              isStepDisabled={isStepDisabled}
              isStepComplete={isStepComplete}
              // @ts-expect-error TS(2345) FIXME: Argument of type 'IShipTpEvent | undefined' is not... Remove this comment to see the full error message
              percentWidth={isLast ? 0 : getPercentage(event, pdads[index + 1].event)}
              isLocationHighlighted={isLocationHighlighted}
              locationText={locationText}
              events={isFirst ? crdEvents : currentEvent}
              isLast={isLast}
              showDelay={showDelay}
            />
          )
        })}
      </Box>
    </>
  )
}
